/**
 * The cells of an Excel workbook's first sheet as text (for the side-by-side run's exceedance workbooks; Diurnal parity
 * C3). Reads the zip's central directory, the shared strings and `xl/worksheets/sheet1.xml`; each cell lands in its own
 * column (an empty cell stays empty). Stored and deflated entries only, which is what Excel, the Diurnal tool and
 * Teslyn Field write. The inflater is the caller's (the browser's `DecompressionStream('deflate-raw')`, Node's zlib in
 * tests), so this file has no imports.
 */

export type InflateRaw = (data: Uint8Array) => Promise<Uint8Array>;

export class XlsxReadError extends Error {}

const u16 = (b: Uint8Array, p: number): number => b[p]! | (b[p + 1]! << 8);
const u32 = (b: Uint8Array, p: number): number => (u16(b, p) | (u16(b, p + 2) << 16)) >>> 0;

const WANTED = new Set(['xl/sharedStrings.xml', 'xl/worksheets/sheet1.xml']);

async function zipEntries(buf: Uint8Array, inflate: InflateRaw): Promise<Map<string, string>> {
  let eocd = buf.length - 22;
  while (eocd >= 0 && u32(buf, eocd) !== 0x06054b50) eocd--;
  if (eocd < 0) throw new XlsxReadError('This is not an Excel workbook (.xlsx).');
  const count = u16(buf, eocd + 10);
  let p = u32(buf, eocd + 16);
  const out = new Map<string, string>();
  const text = new TextDecoder('utf-8');
  for (let k = 0; k < count; k++) {
    if (u32(buf, p) !== 0x02014b50) throw new XlsxReadError('The workbook is damaged.');
    const method = u16(buf, p + 10);
    const size = u32(buf, p + 20);
    const nameLen = u16(buf, p + 28);
    const extra = u16(buf, p + 30);
    const comment = u16(buf, p + 32);
    const local = u32(buf, p + 42);
    const name = text.decode(buf.subarray(p + 46, p + 46 + nameLen));
    if (WANTED.has(name)) {
      const lnl = u16(buf, local + 26);
      const lex = u16(buf, local + 28);
      const data = buf.subarray(local + 30 + lnl + lex, local + 30 + lnl + lex + size);
      if (method === 0) out.set(name, text.decode(data));
      else if (method === 8) out.set(name, text.decode(await inflate(data)));
      else throw new XlsxReadError('The workbook is packed in a way Teslyn cannot read.');
    }
    p += 46 + nameLen + extra + comment;
  }
  return out;
}

const decode = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCharCode(Number(d)))
    .replace(/&amp;/g, '&');

/** "A" → 0, "J" → 9, "AA" → 26. */
function columnIndex(letters: string): number {
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** The first sheet's rows, each cell as text in its column. */
export async function xlsxRows(bytes: Uint8Array, inflate: InflateRaw): Promise<string[][]> {
  const files = await zipEntries(bytes, inflate);
  const sheet = files.get('xl/worksheets/sheet1.xml');
  if (sheet === undefined) throw new XlsxReadError('The workbook has no first sheet.');
  const shared = [...(files.get('xl/sharedStrings.xml') ?? '').matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) =>
    decode([...m[1]!.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join('')),
  );
  const rows: string[][] = [];
  for (const r of sheet.matchAll(/<row[^>]*>([\s\S]*?)<\/row>|<row[^>]*\/>/g)) {
    const cells: string[] = [];
    for (const c of (r[1] ?? '').matchAll(/<c r="([A-Z]+)\d+"([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = c[2]!;
      const body = c[3] ?? '';
      const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
      const inline = [...body.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join('');
      const value =
        /t="s"/.test(attrs) && v !== undefined ? (shared[Number(v)] ?? '') : decode(inline || v || '');
      const col = columnIndex(c[1]!);
      while (cells.length < col) cells.push('');
      cells[col] = value;
    }
    rows.push(cells);
  }
  return rows;
}
